import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, FileText, ShieldCheck, Plane, Stamp, PackageCheck, Truck, Plus, Trash2, Loader2, Check, Lock } from "lucide-react";
import api, { formatError } from "@/lib/api";
import StatusStepper from "@/components/StatusStepper";
import { SharePanel } from "@/components/SharePanel";
import { PreAlertPanel } from "@/components/shipment/PreAlertPanel";
import { TransmittalPanel } from "@/components/shipment/TransmittalPanel";
import { CashAdvancePanel } from "@/components/shipment/CashAdvancePanel";
import { CostSummary } from "@/components/shipment/CostSummary";
import { ReportButtons } from "@/components/shipment/ReportButtons";
import { ShipmentAdminActions } from "@/components/shipment/ShipmentAdminActions";
import { AogTag } from "@/components/shipment/AogTag";
import { LiquidationPanel } from "@/components/shipment/LiquidationPanel";
import { useAuth } from "@/context/AuthContext";
import { STAGES, stagesFor, stageAccess, SUPERVISORS, ROLE_BADGE, CUSTOMS_BADGE, DELIVERY_BADGE, FWD_STATUSES } from "@/lib/stages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const Section = ({ title, icon: Icon, right, children }) => (
  <div className="glass-card rounded-lg p-6">
    <div className="flex items-center justify-between mb-5 gap-4">
      <div className="flex items-center gap-2 font-display text-xl font-bold">
        <Icon size={18} className="text-sky-400" /> {title}
      </div>
      {right}
    </div>
    {children}
  </div>
);

const Row = ({ label, value, mono }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-slate-800/70 last:border-0">
    <dt className="text-xs text-slate-500 uppercase tracking-widest">{label}</dt>
    <dd className={`text-sm text-slate-200 text-right ${mono ? "font-mono-ops" : ""}`}>{value || "—"}</dd>
  </div>
);

const LockedNotice = ({ reason }) => (
  <div data-testid="locked-notice" className="flex items-center gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2">
    <Lock size={13} /> {reason}
  </div>
);

const SaveBtn = ({ onClick, loading, label = "Save", testid }) => (
  <Button data-testid={testid} onClick={onClick} disabled={loading} className="bg-blue-600 hover:bg-blue-500">
    {loading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Check size={14} className="mr-2" />} {label}
  </Button>
);

export default function ShipmentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [sh, setSh] = useState(null);
  const [users, setUsers] = useState([]);
  const [denied, setDenied] = useState("");
  const [params] = useSearchParams();

  const load = async () => {
    try {
      const [s, u] = await Promise.all([api.get(`/shipments/${id}`), api.get("/users")]);
      setSh(s.data); setUsers(u.data);
    } catch (e) {
      if ([403, 404].includes(e.response?.status)) setDenied(formatError(e)); else toast.error(formatError(e));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (denied) return <div data-testid="shipment-denied" className="glass-card rounded-lg p-6 text-slate-300">{denied} <Link to="/shipments" className="text-sky-300 underline ml-1">Back to shipments</Link></div>;
  if (!sh) return <div className="text-slate-400">Loading shipment…</div>;
  const stages = stagesFor(sh.type);
  const folder = sh.type || "brokerage";

  return (
    <div className="space-y-6">
      <Link to={`/shipments?type=${folder}`} className="text-slate-400 hover:text-slate-200 inline-flex items-center gap-1">
        <ArrowLeft size={16} /> Back to {folder === "forwarding" ? "Forwarding" : "Brokerage"}
      </Link>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div data-testid="shipment-folder" className="text-xs uppercase tracking-[0.3em] text-sky-400 mb-1">{folder} shipment</div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 data-testid="shipment-number" className="font-display text-4xl font-bold font-mono-ops">{sh.shipment_number}</h1>
            <AogTag s={sh} testid="detail-aog-tag" />
          </div>
          <p className="text-slate-400 text-sm mt-1">
            {sh.documentation?.client_name || sh.pre_alert?.subject || "—"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <ReportButtons sh={sh} />
          {user?.role === "admin" && !sh.archived && (
            <ShipmentAdminActions sh={sh} archived={false} onDone={() => { window.location.href = `/shipments?type=${folder}`; }} />
          )}
          <div className="text-xs uppercase tracking-widest text-slate-500">Current stage</div>
          <span className="px-3 py-1 rounded border border-blue-500/40 bg-blue-500/10 text-sky-300 text-xs uppercase tracking-widest">
            {STAGES.find((s) => s.key === sh.current_stage)?.label}
          </span>
        </div>
      </div>

      <div className="glass-card rounded-lg p-5">
        <StatusStepper currentStage={sh.current_stage} stages={stages} />
      </div>

      <Tabs defaultValue={params.get("tab") === "liquidation" ? "liquidation" : "pre_alert"} className="w-full">
        <TabsList className="bg-slate-900 border border-slate-800 h-auto p-1 flex-wrap gap-1">
          {stages.map((s) => <TabsTrigger key={s.key} data-testid={`tab-${s.key}`} value={s.key}>{s.label}</TabsTrigger>)}
          <TabsTrigger data-testid="tab-liquidation" value="liquidation">Liquidation</TabsTrigger>
        </TabsList>

        <TabsContent value="pre_alert" className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <PreAlertPanel key={sh.updated_at} sh={sh} user={user} onSaved={load} />
            <SharePanel sh={sh} onSaved={load} />
            <CostSummary sh={sh} />
          </div>
          <Section title="Timeline" icon={ShieldCheck}>
            <ol className="space-y-3">
              {(sh.timeline || []).slice().reverse().map((t, i) => (
                <li key={i} className="relative pl-6">
                  <div className="absolute left-0 top-1.5 w-2.5 h-2.5 rounded-full bg-sky-500" />
                  <div className="text-sm text-slate-200">{t.label}</div>
                  <div className="text-xs text-slate-500">
                    {new Date(t.at).toLocaleString()} · {t.actor_name}{" "}
                    <span className={`ml-1 px-1 rounded ${ROLE_BADGE[t.actor_role] || ""}`}>{t.actor_role}</span>
                  </div>
                  {t.note && <div className="text-xs text-slate-400 mt-1">{t.note}</div>}
                </li>
              ))}
            </ol>
          </Section>
        </TabsContent>

        {stages.filter((s) => s.key !== "pre_alert").map((s) => {
          const Panel = PANELS[s.key];
          return (
            <TabsContent key={s.key} value={s.key} className="mt-5">
              <Panel key={sh.updated_at} sh={sh} access={stageAccess(user, sh, s.key)} users={users} user={user} onSaved={load} />
            </TabsContent>
          );
        })}
        <TabsContent value="liquidation" className="mt-5"><LiquidationPanel sh={sh} /></TabsContent>
      </Tabs>
    </div>
  );
}

const SLOTS = [["declarant", "Assigned Declarant", "Customs Declaration"], ["processor", "Assigned Processor", "Processing & Release"], ["delivery", "Assigned Delivery", "Delivery"]];

function AssignSelects({ sh, f, setF, users, user, canEdit }) {
  return (
    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-950/40 border border-slate-800 rounded-md p-4">
      {SLOTS.map(([slot, label, step]) => {
        const locked = !canEdit || (sh.assignments?.[slot] && !SUPERVISORS.includes(user?.role));
        return (
          <div key={slot}>
            <Label>{label}</Label>
            <Select value={f[`assigned_${slot}_id`]} onValueChange={(v) => setF({ ...f, [`assigned_${slot}_id`]: v })} disabled={locked}>
              <SelectTrigger data-testid={`select-assign-${slot}`} className="bg-slate-950 border-slate-700"><SelectValue placeholder={`Choose ${slot}`} /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                {users.filter((u) => u.role === slot).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-slate-500 mt-1">Works on {step}{sh.assignments?.[slot] && !SUPERVISORS.includes(user?.role) ? " · only Admin/Ops can change" : ""}</div>
          </div>
        );
      })}
    </div>
  );
}

function ForwardingAssign({ f, setF, users, canEdit }) {
  return (
    <div data-testid="fwd-doc-assign" className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-950/40 border border-slate-800 rounded-md p-4">
      <div>
        <Label>Assigned CS</Label>
        <Select value={f.assigned_cs_id} onValueChange={(v) => setF({ ...f, assigned_cs_id: v })} disabled={!canEdit}>
          <SelectTrigger data-testid="select-assign-cs" className="bg-slate-950 border-slate-700"><SelectValue placeholder="Choose CS" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
            {users.filter((u) => u.role === "broker_csr").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Status</Label>
        <Select value={f.fwd_status} onValueChange={(v) => setF({ ...f, fwd_status: v })} disabled={!canEdit}>
          <SelectTrigger data-testid="select-fwd-status" className="bg-slate-950 border-slate-700"><SelectValue placeholder="Choose status" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
            {FWD_STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {f.fwd_status === "Other" && (
        <div>
          <Label>Specify status</Label>
          <Input data-testid="input-fwd-status-other" value={f.fwd_status_other} onChange={(e) => setF({ ...f, fwd_status_other: e.target.value })}
                 disabled={!canEdit} className="bg-slate-950 border-slate-700" />
        </div>
      )}
    </div>
  );
}

// ------- Documentation (CSR Broker) -------
const DEFAULT_REQS = [
  { name: "Air Waybill (AWB) / Bill of Lading", required: true, submitted: false },
  { name: "Commercial Invoice", required: true, submitted: false },
  { name: "Packing List", required: true, submitted: false },
  { name: "Import Permit", required: false, submitted: false },
];

function DocumentationPanel({ sh, access, users, user, onSaved }) {
  const canEdit = access.ok;
  const fwd = sh.type === "forwarding";
  const d = sh.documentation || {};
  const [f, setF] = useState({
    client_name: d.client_name || sh.pre_alert?.client_hint || sh.pre_alert?.sender_name || "",
    mawb_number: d.mawb_number || sh.pre_alert?.mawb_hint || "", hawb_number: d.hawb_number || sh.pre_alert?.hawb_hint || "",
    shipper: d.shipper || "", consignee: d.consignee || "",
    origin: d.origin || sh.pre_alert?.origin_hint || "", destination: d.destination || "",
    cargo_category: d.cargo_category || (d.cargo_description ? (d.cargo_description === "Aircraft Materials" ? "Aircraft Materials" : "Others") : ""),
    cargo_description: d.cargo_description || "",
    assigned_declarant_id: sh.assignments?.declarant?.id || "",
    assigned_processor_id: sh.assignments?.processor?.id || "",
    assigned_delivery_id: sh.assignments?.delivery?.id || "",
    assigned_cs_id: sh.assignments?.cs?.id || "",
    fwd_status: d.fwd_status || "", fwd_status_other: d.fwd_status_other || "",
    notes: d.notes || "",
  });
  const [reqs, setReqs] = useState(d.requirements?.length ? d.requirements : DEFAULT_REQS);
  const [newReq, setNewReq] = useState("");
  const [loading, setLoading] = useState(false);

  const addReq = () => { if (!newReq) return; setReqs([...reqs, { name: newReq, required: false, submitted: false }]); setNewReq(""); };
  const upd = (i, k) => { const n = [...reqs]; n[i] = { ...n[i], [k]: !n[i][k] }; setReqs(n); };
  const rem = (i) => setReqs(reqs.filter((_, idx) => idx !== i));

  const save = async () => {
    if (fwd && f.fwd_status === "Other" && !f.fwd_status_other.trim()) { toast.error("Please specify the status"); return; }
    setLoading(true);
    try {
      await api.post(`/shipments/${sh.id}/documentation`, { ...f, requirements: reqs });
      toast.success("Documentation saved"); onSaved();
    } catch (e) { toast.error(formatError(e)); } finally { setLoading(false); }
  };

  const fld = (key, label, testid) => (
    <div>
      <Label>{label}</Label>
      <Input data-testid={testid} value={f[key]} onChange={(e) => setF({...f, [key]: e.target.value})}
             disabled={!canEdit} className="bg-slate-950 border-slate-700" />
    </div>
  );

  return (
    <Section title={fwd ? "Documentation" : "Documentation (CSR)"} icon={FileText}>
      {!canEdit && <div className="mb-4"><LockedNotice reason={access.reason} /></div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fld("client_name", "Client", "input-doc-client")}
        {fld("mawb_number", "MAWB / BOL #", "input-doc-mawb")}
        {fld("hawb_number", "HAWB #", "input-doc-hawb")}
        {fld("shipper", "Shipper", "input-doc-shipper")}
        {fld("consignee", "Consignee", "input-doc-consignee")}
        {fld("origin", "Origin", "input-doc-origin")}
        {fld("destination", "Destination", "input-doc-destination")}
        {fwd ? <ForwardingAssign f={f} setF={setF} users={users} canEdit={canEdit} />
             : <AssignSelects sh={sh} f={f} setF={setF} users={users} user={user} canEdit={canEdit} />}
        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Cargo description</Label>
            <Select value={f.cargo_category} onValueChange={(v) => setF({ ...f, cargo_category: v, cargo_description: v === "Aircraft Materials" ? v : "" })} disabled={!canEdit}>
              <SelectTrigger data-testid="select-cargo-category" className="bg-slate-950 border-slate-700"><SelectValue placeholder="Choose cargo type" /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                {["Aircraft Materials", "Others"].map((c) => <SelectItem key={c} value={c}>{c === "Others" ? "Others (Specify)" : c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {f.cargo_category === "Others" && (
            <div>
              <Label>Specify cargo</Label>
              <Input data-testid="input-doc-cargo" value={f.cargo_description} onChange={(e) => setF({ ...f, cargo_description: e.target.value })}
                     disabled={!canEdit} className="bg-slate-950 border-slate-700" />
            </div>
          )}
        </div>
      </div>

      <div className="mt-6">
        <div className="font-display text-lg font-bold mb-2">Documentation checklist</div>
        <div className="space-y-2 overflow-x-auto pb-1">
          {reqs.map((it, i) => (
            <div key={i} data-testid={`req-row-${i}`} className="flex items-center gap-3 bg-slate-950/60 border border-slate-800 rounded-md p-3">
              <Checkbox data-testid={`req-submitted-${i}`} checked={it.submitted} onCheckedChange={() => upd(i, "submitted")} disabled={!canEdit} />
              <div className="flex-1">
                <div className="text-sm text-slate-200">{it.name}</div>
                <div className="text-xs text-slate-500">{it.required ? "Required" : "Optional"}</div>
              </div>
              <label className="text-xs text-slate-400 flex items-center gap-2">
                <Checkbox data-testid={`req-required-${i}`} checked={it.required} onCheckedChange={() => upd(i, "required")} disabled={!canEdit} />
                Required
              </label>
              {canEdit && (
                <Button data-testid={`req-remove-${i}`} size="sm" variant="ghost" onClick={() => rem(i)} className="text-rose-300 hover:bg-rose-500/10">
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
          ))}
        </div>
        {canEdit && (
          <div className="flex gap-2 mt-3">
            <Input data-testid="input-new-req" placeholder="Add requirement…" value={newReq} onChange={(e) => setNewReq(e.target.value)}
                   className="bg-slate-950 border-slate-700" />
            <Button data-testid="button-add-req" variant="outline" onClick={addReq} className="border-slate-700 hover:bg-slate-800"><Plus size={14} /></Button>
          </div>
        )}
      </div>

      <div className="mt-4">
        <Label>Notes</Label>
        <Textarea data-testid="input-doc-notes" rows={2} value={f.notes} onChange={(e) => setF({...f, notes: e.target.value})}
                  disabled={!canEdit} className="bg-slate-950 border-slate-700" />
      </div>
      {canEdit && <div className="mt-4"><SaveBtn testid="button-save-documentation" onClick={save} loading={loading} /></div>}
    </Section>
  );
}

// ------- Customs (Declarant) -------
const mawbOf = (sh) => sh.documentation?.mawb_number || sh.pre_alert?.mawb_hint || "";

const AssignedBanner = ({ sh, slot, label }) => {
  const a = sh.assignments?.[slot];
  return (
    <div data-testid={`assigned-${slot}-banner`} className="mb-5 flex items-center justify-between gap-3 bg-blue-600/10 border border-blue-500/30 rounded-md px-4 py-3">
      <span className="text-[10px] uppercase tracking-widest text-slate-400">{label}</span>
      <span className="text-sm font-medium text-sky-200">{a?.name || "Not assigned yet"}</span>
    </div>
  );
};

function CustomsPanel({ sh, access, onSaved }) {
  const canEdit = access.ok;
  const c = sh.customs_declaration || {};
  const [status, setStatus] = useState(c.status || "Pending");
  const [ipNumber, setIpNumber] = useState(c.ip_number || "");
  const [boc, setBoc] = useState(c.boc_entry_number || "");
  const [notes, setNotes] = useState(c.notes || "");
  const [loading, setLoading] = useState(false);

  const save = async () => {
    setLoading(true);
    try {
      await api.post(`/shipments/${sh.id}/customs`, { ip_number: ipNumber, boc_entry_number: boc, status, notes });
      toast.success(`Customs: ${status}`); onSaved();
    } catch (e) { toast.error(formatError(e)); } finally { setLoading(false); }
  };

  return (
    <Section title="Customs Declaration" icon={ShieldCheck}
             right={c.status ? <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${CUSTOMS_BADGE[c.status]}`}>{c.status}</span> : null}>
      <AssignedBanner sh={sh} slot="declarant" label="Assigned Declarant" />
      {!canEdit && <div className="mb-4"><LockedNotice reason={access.reason} /></div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>MAWB # (from Documentation)</Label>
          <Input data-testid="customs-mawb" readOnly value={mawbOf(sh)} placeholder="Set in Documentation" className="bg-slate-950 border-slate-700 font-mono-ops text-slate-400" />
        </div>
        <div>
          <Label>Status</Label>
          <Select value={status} onValueChange={setStatus} disabled={!canEdit}>
            <SelectTrigger data-testid="select-customs-status" className="bg-slate-950 border-slate-700"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              {["Pending", "Lodged", "Released", "On Hold"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>IP Number</Label>
          <Input data-testid="input-ip-number" value={ipNumber} onChange={(e) => setIpNumber(e.target.value)}
                 disabled={!canEdit} className="bg-slate-950 border-slate-700 font-mono-ops" />
        </div>
        <div>
          <Label>BOC Entry Number</Label>
          <Input data-testid="input-boc" value={boc} onChange={(e) => setBoc(e.target.value)}
                 disabled={!canEdit} className="bg-slate-950 border-slate-700 font-mono-ops" />
        </div>
        <div className="md:col-span-2">
          <Label>Notes</Label>
          <Textarea data-testid="input-customs-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                    disabled={!canEdit} className="bg-slate-950 border-slate-700" />
        </div>
      </div>
      {canEdit && <div className="mt-4"><SaveBtn testid="button-save-customs" onClick={save} loading={loading} /></div>}
    </Section>
  );
}

// ------- Logistics (Forwarder) -------
function LogisticsPanel({ sh, access, onSaved }) {
  const canEdit = access.ok;
  const l = sh.logistics || {};
  const [f, setF] = useState({
    mode: l.mode || "Air", carrier: l.carrier || "", booking_ref: l.booking_ref || "",
    vessel_flight: l.vessel_flight || "", etd: l.etd || "", eta: l.eta || "",
    dispatch_schedule: l.dispatch_schedule || "", tracking_number: l.tracking_number || "", notes: l.notes || "",
  });
  const [loading, setLoading] = useState(false);
  const save = async () => {
    setLoading(true);
    try { await api.post(`/shipments/${sh.id}/logistics`, f); toast.success("Logistics saved"); onSaved(); }
    catch (e) { toast.error(formatError(e)); } finally { setLoading(false); }
  };
  const fld = (key, label, type = "text", testid) => (
    <div>
      <Label>{label}</Label>
      <Input data-testid={testid} type={type} value={f[key]} onChange={(e) => setF({...f, [key]: e.target.value})}
             disabled={!canEdit} className="bg-slate-950 border-slate-700" />
    </div>
  );
  return (
    <Section title="Booking / Logistics" icon={Plane}>
      {!canEdit && <div className="mb-4"><LockedNotice reason={access.reason} /></div>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>Mode</Label>
          <Select value={f.mode} onValueChange={(v) => setF({...f, mode: v})} disabled={!canEdit}>
            <SelectTrigger data-testid="select-mode" className="bg-slate-950 border-slate-700"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              {["Air", "Sea", "Land"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {fld("carrier", "Carrier (Shipping line / Airline)", "text", "input-carrier")}
        {fld("vessel_flight", "Vessel / Flight #", "text", "input-vessel")}
        {fld("booking_ref", "Booking reference", "text", "input-booking")}
        {fld("etd", "ETD", "datetime-local", "input-etd")}
        {fld("eta", "ETA", "datetime-local", "input-eta")}
        {fld("dispatch_schedule", "Dispatch schedule", "text", "input-dispatch")}
        {fld("tracking_number", "Tracking #", "text", "input-tracking")}
      </div>
      <div className="mt-4">
        <Label>Notes</Label>
        <Textarea data-testid="input-logistics-notes" rows={2} value={f.notes} onChange={(e) => setF({...f, notes: e.target.value})}
                  disabled={!canEdit} className="bg-slate-950 border-slate-700" />
      </div>
      {canEdit && <div className="mt-4"><SaveBtn testid="button-save-logistics" onClick={save} loading={loading} /></div>}
    </Section>
  );
}

// ------- Processing (Processors) -------
function ProcessingPanel({ sh, access, onSaved }) {
  const canEdit = access.ok;
  const p = sh.processing || {};
  const [subs, setSubs] = useState(p.submissions?.length ? p.submissions : [{ agency: "BOC", reference: "", submitted_at: "", status: "Pending", notes: "" }]);
  const [notes, setNotes] = useState(p.notes || "");
  const [loading, setLoading] = useState(false);

  const addSub = () => setSubs([...subs, { agency: "BOC", reference: "", submitted_at: "", status: "Pending", notes: "" }]);
  const upd = (i, k, v) => { const n = [...subs]; n[i] = {...n[i], [k]: v}; setSubs(n); };
  const rem = (i) => setSubs(subs.filter((_, idx) => idx !== i));

  const save = async () => {
    setLoading(true);
    try { await api.post(`/shipments/${sh.id}/processing`, { submissions: subs, notes }); toast.success("Processing saved"); onSaved(); }
    catch (e) { toast.error(formatError(e)); } finally { setLoading(false); }
  };

  return (
    <Section title="Processing (Government submissions)" icon={Stamp}>
      <AssignedBanner sh={sh} slot="processor" label="Assigned Processor" />
      {!canEdit && <div className="mb-4"><LockedNotice reason={access.reason} /></div>}
      <div className="space-y-2 overflow-x-auto pb-1">
        <div className="grid grid-cols-12 gap-2 min-w-[680px] text-[10px] uppercase tracking-widest text-slate-500">
          <span className="col-span-2">Agency</span><span className="col-span-3">MAWB #</span><span className="col-span-2">Submitted</span>
          <span className="col-span-2">Status</span><span className="col-span-2">Notes</span>
        </div>
        {subs.map((s, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 min-w-[680px]">
            <Select value={s.agency} onValueChange={(v) => upd(i, "agency", v)} disabled={!canEdit}>
              <SelectTrigger data-testid={`sub-agency-${i}`} className="col-span-2 bg-slate-950 border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                {["BOC", "BIR", "DTI", "Other"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid={`sub-mawb-${i}`} readOnly value={mawbOf(sh)} placeholder="MAWB (from Documentation)" title="MAWB # from Documentation"
                   className="col-span-3 bg-slate-950 border-slate-700 font-mono-ops text-slate-400" />
            <Input data-testid={`sub-date-${i}`} type="date" value={s.submitted_at?.slice(0,10) || ""} onChange={(e) => upd(i, "submitted_at", e.target.value)}
                   disabled={!canEdit} className="col-span-2 bg-slate-950 border-slate-700" />
            <Select value={s.status} onValueChange={(v) => upd(i, "status", v)} disabled={!canEdit}>
              <SelectTrigger data-testid={`sub-status-${i}`} className="col-span-2 bg-slate-950 border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                {["Pending", "Submitted", "Approved", "Rejected"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid={`sub-notes-${i}`} value={s.notes} onChange={(e) => upd(i, "notes", e.target.value)} disabled={!canEdit}
                   placeholder="Notes" className="col-span-2 bg-slate-950 border-slate-700" />
            {canEdit && (
              <Button data-testid={`sub-remove-${i}`} size="sm" variant="ghost" onClick={() => rem(i)} className="col-span-1 text-rose-300 hover:bg-rose-500/10">
                <Trash2 size={14} />
              </Button>
            )}
          </div>
        ))}
      </div>
      {canEdit && (
        <Button data-testid="button-add-sub" variant="outline" size="sm" onClick={addSub} className="mt-3 border-slate-700 hover:bg-slate-800">
          <Plus size={14} className="mr-1" /> Add submission
        </Button>
      )}
      <div className="mt-4">
        <Label>Notes</Label>
        <Textarea data-testid="input-processing-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                  disabled={!canEdit} className="bg-slate-950 border-slate-700" />
      </div>
      {canEdit && <div className="mt-4"><SaveBtn testid="button-save-processing" onClick={save} loading={loading} /></div>}
    </Section>
  );
}

// ------- Release (Processors) -------
function ReleasePanel({ sh, access, onSaved }) {
  const canEdit = access.ok;
  const r = sh.release || {};
  const [f, setF] = useState({
    release_date: r.release_date || "",
    or_number: r.or_number || "",
    storage_amount: r.storage_amount ?? "",
    notes: r.notes || "",
  });
  const [loading, setLoading] = useState(false);
  const save = async () => {
    setLoading(true);
    try { await api.post(`/shipments/${sh.id}/release`, { ...f, storage_amount: parseFloat(f.storage_amount) || 0 }); toast.success("Release recorded"); onSaved(); }
    catch (e) { toast.error(formatError(e)); } finally { setLoading(false); }
  };
  return (
    <Section title="Release" icon={PackageCheck}>
      {!canEdit && <div className="mb-4"><LockedNotice reason={access.reason} /></div>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>Release date</Label>
          <Input data-testid="input-release-date" type="date" value={f.release_date?.slice(0,10) || ""}
                 onChange={(e) => setF({...f, release_date: e.target.value})}
                 disabled={!canEdit} className="bg-slate-950 border-slate-700" />
        </div>
        <div>
          <Label>OR number</Label>
          <Input data-testid="input-or-number" value={f.or_number} onChange={(e) => setF({...f, or_number: e.target.value})}
                 disabled={!canEdit} className="bg-slate-950 border-slate-700 font-mono-ops" />
        </div>
        <div>
          <Label>Storage amount (not sent to Finance)</Label>
          <Input data-testid="input-storage-amount" type="number" min="0" value={f.storage_amount} onChange={(e) => setF({...f, storage_amount: e.target.value})}
                 disabled={!canEdit} className="bg-slate-950 border-slate-700 font-mono-ops" />
        </div>
        <div className="md:col-span-3">
          <Label>Notes</Label>
          <Textarea data-testid="input-release-notes" rows={2} value={f.notes} onChange={(e) => setF({...f, notes: e.target.value})}
                    disabled={!canEdit} className="bg-slate-950 border-slate-700" />
        </div>
      </div>
      {canEdit && <div className="mt-4"><SaveBtn testid="button-save-release" onClick={save} loading={loading} /></div>}
      <CashAdvancePanel sh={sh} canEdit={canEdit} />
    </Section>
  );
}

// ------- Delivery -------
function DeliveryPanel({ sh, access, onSaved }) {
  const canEdit = access.ok;
  const d = sh.delivery_info || {};
  const [f, setF] = useState({
    trucking_company: d.trucking_company || "", driver_name: d.driver_name || "",
    plate_number: d.plate_number || "", scheduled_date: d.scheduled_date || "",
    status: d.status || "Scheduled", delivered_at: d.delivered_at || "",
    dr_serial_number: d.dr_serial_number || d.pod_reference || "", dr_notes: d.dr_notes || d.pod_notes || "",
  });
  const [helpers, setHelpers] = useState(d.helpers?.length ? d.helpers : [""]);
  const [loading, setLoading] = useState(false);
  const save = async () => {
    setLoading(true);
    try { await api.post(`/shipments/${sh.id}/delivery`, { ...f, helpers: helpers.filter((h) => h.trim()) }); toast.success("Delivery updated"); onSaved(); }
    catch (e) { toast.error(formatError(e)); } finally { setLoading(false); }
  };
  const fld = (key, label, type = "text", testid) => (
    <div>
      <Label>{label}</Label>
      <Input data-testid={testid} type={type} value={type.startsWith("date") ? (f[key]?.slice(0, type === "datetime-local" ? 16 : 10) || "") : f[key]}
             onChange={(e) => setF({...f, [key]: e.target.value})}
             disabled={!canEdit} className="bg-slate-950 border-slate-700" />
    </div>
  );
  return (
    <Section title="Delivery" icon={Truck}
             right={d.status ? <span data-testid="delivery-status" className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${DELIVERY_BADGE[d.status]}`}>{d.status}</span> : null}>
      {(sh.type || "brokerage") === "brokerage" && <AssignedBanner sh={sh} slot="delivery" label="Assigned Delivery" />}
      {!canEdit && <div className="mb-4"><LockedNotice reason={access.reason} /></div>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>Status</Label>
          <Select value={f.status} onValueChange={(v) => setF({...f, status: v})} disabled={!canEdit}>
            <SelectTrigger data-testid="select-delivery-status" className="bg-slate-950 border-slate-700"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              {["Scheduled", "In Transit", "Delivered", "Failed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {fld("trucking_company", "Trucking company", "text", "input-trucking")}
        {fld("driver_name", "Driver name", "text", "input-driver")}
        {helpers.map((h, i) => (
          <div key={i}>
            <Label>Helper {i + 1}</Label>
            <div className="flex gap-2">
              <Input data-testid={`input-helper-${i}`} value={h} onChange={(e) => setHelpers(helpers.map((x, idx) => (idx === i ? e.target.value : x)))}
                     disabled={!canEdit} className="bg-slate-950 border-slate-700" />
              {canEdit && helpers.length > 1 && (
                <Button data-testid={`remove-helper-${i}`} size="sm" variant="ghost" onClick={() => setHelpers(helpers.filter((_, idx) => idx !== i))} className="text-rose-300 hover:bg-rose-500/10"><Trash2 size={14} /></Button>
              )}
            </div>
          </div>
        ))}
        {canEdit && helpers.length < 2 && (
          <div className="flex items-end">
            <Button data-testid="button-add-helper" variant="outline" size="sm" onClick={() => setHelpers([...helpers, ""])} className="border-slate-700 hover:bg-slate-800">
              <Plus size={14} className="mr-1" /> Add helper
            </Button>
          </div>
        )}
        {fld("plate_number", "Plate #", "text", "input-plate")}
        {fld("scheduled_date", "Scheduled", "datetime-local", "input-scheduled")}
        {fld("delivered_at", "Delivered at", "datetime-local", "input-delivered-at")}
        {fld("dr_serial_number", "DR serial number", "text", "input-dr-serial")}
        <div className="md:col-span-3">
          <Label>DR / Delivery notes</Label>
          <Textarea data-testid="input-dr-notes" rows={2} value={f.dr_notes} onChange={(e) => setF({...f, dr_notes: e.target.value})}
                    disabled={!canEdit} className="bg-slate-950 border-slate-700" />
        </div>
      </div>
      {canEdit && <div className="mt-4"><SaveBtn testid="button-save-delivery" onClick={save} loading={loading} /></div>}
    </Section>
  );
}

const PANELS = {
  documentation: DocumentationPanel, customs_declaration: CustomsPanel, logistics: LogisticsPanel,
  processing: ProcessingPanel, release: ReleasePanel, delivery: DeliveryPanel, transmittal: TransmittalPanel,
};
